import RoadWorker from "./roadWorker?worker";
import { apiUrl } from "./api";

/**
 * fetchRoadData — fetch + parse a backend JSON payload off the main
 * thread via a Web Worker (see roadWorker.ts). Falls back to a plain
 * main-thread fetch if the WebView can't construct the worker.
 *
 * Used for /api/nearby-roads, whose Overpass response is large enough
 * that main-thread JSON.parse caused pan stalls while driving.
 */

type Pending = { resolve: (d: unknown) => void; reject: (e: unknown) => void };

let worker: Worker | null = null;
let workerBroken = false;
const pending = new Map<number, Pending>();
let nextId = 1;

function ensureWorker(): Worker | null {
  if (workerBroken) return null;
  if (worker) return worker;
  try {
    worker = new RoadWorker();
    worker.onmessage = (e: MessageEvent<{ id: number; ok: boolean; data?: unknown; error?: string }>) => {
      const p = pending.get(e.data.id);
      if (!p) return;
      pending.delete(e.data.id);
      if (e.data.ok) p.resolve(e.data.data);
      else p.reject(new Error(e.data.error || "road worker error"));
    };
    worker.onerror = () => {
      // Worker blew up (e.g. CSP / unsupported). Reject everything in
      // flight and fall back to main-thread fetch from here on.
      workerBroken = true;
      for (const p of pending.values()) p.reject(new Error("road worker error"));
      pending.clear();
      worker = null;
    };
  } catch {
    workerBroken = true;
    worker = null;
  }
  return worker;
}

export function fetchRoadData<T>(path: string, signal?: AbortSignal): Promise<T> {
  const url = apiUrl(path);
  const w = ensureWorker();
  if (!w) {
    return fetch(url, { signal }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json() as Promise<T>;
    });
  }
  const id = nextId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (d: unknown) => void, reject });
    if (signal) {
      signal.addEventListener(
        "abort",
        () => { if (pending.delete(id)) reject(new DOMException("Aborted", "AbortError")); },
        { once: true },
      );
    }
    w.postMessage({ id, url });
  });
}
